import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';
import { prefs } from '@/preferences';
import { type BooleanPrefKey, endOfFinishedUnit, type PrefToKeep, prefsToRestore, recordPrefWrite } from '@/tutorial/prefLedger';
import type { PostAuthMarker } from '@/tutorial/tutorialOffer';
import { type ActiveUnitRecord, parseActiveRecord } from '@/tutorial/tutorialUnits';
import type { RunOutcome, TutorialUnit } from '@/types';
import { prefStorage } from './storage';

/** A tutorial unit (the core or a chapter) the user started, bound to the
 *  project it runs in. Set by «Начать» before the hop into the Editor; the
 *  paper's `TutorialRunner` picks it up there. Session-only: what survives a reload is
 *  `tutorialActiveUnitAtom`, and the run restarts from it. */
export interface TutorialRun {
    unit: TutorialUnit;
    projectId: number;
    /** Set once the run has ended — finished, or left with «Выйти» — and the
     *  closing card is up. Kept here, not in the runner, so a paper remount
     *  shows the card again instead of restarting the unit. */
    ended?: RunOutcome;
    /** The unit was already active: a reload, or the resume form's «Продолжить». */
    resumed?: boolean;
    /** A finished unit's question «Оставить эти настройки?»: the prefs a step
     *  had the user set, left different from before. Cleared once answered. */
    keepPrefs?: PrefToKeep[];
}

export const tutorialRunAtom = atom<TutorialRun | null>(null);

/** `?tutorial` was seen in this browser session (#1848 ticket 12): the
 *  tutorial stays on behind its flag. Session-only, so a navigation that drops
 *  the query keeps the override and a new browser session starts without it. */
export const tutorialOverrideAtom = atomWithStorage(
    'tutorial-override',
    false,
    createJSONStorage<boolean>(() => sessionStorage),
    { getOnInit: true },
);

/** Right after sign-up or login (#1848 ticket 18): the offer card shows on
 *  the screen the user lands on. Session-only, so it outlives the reload the
 *  login form makes. See `nextPostAuthMarker` for when it ends. */
export const tutorialPostAuthAtom = atomWithStorage<PostAuthMarker | null>(
    'tutorial-post-auth',
    null,
    createJSONStorage<PostAuthMarker | null>(() => sessionStorage),
    { getOnInit: true },
);

/** The offer was answered in this page, on whichever surface: no other
 *  surface offers it again while the write is in flight. */
export const tutorialOfferAnsweredAtom = atom(false);

/** A sign-up or login: the offer is due on the screen the user lands on. */
export const tutorialMarkSignedInAtom = atom(null, (_get, set) => {
    set(tutorialPostAuthAtom, { shownAt: null });
    set(tutorialOfferAnsweredAtom, false);
});

/** The step on screen, 0-based — `null` while no step is being watched (no
 *  run, or the run is still settling its setup). The rest of the app reads it
 *  to stay out of the way: hints don't start over a step (#1853). */
export const tutorialActiveStepAtom = atom<number | null>(null);

const activeRecordStorage: SyncStorage<ActiveUnitRecord | null> = {
    getItem: key => parseActiveRecord(prefStorage.getItem(key)),
    setItem: (key, value) => (value === null ? prefStorage.removeItem(key) : prefStorage.setItem(key, JSON.stringify(value))),
    removeItem: key => prefStorage.removeItem(key),
};

/** The active unit and the pref writes it has to undo, stored as one record. */
const activeRecordAtom = atomWithStorage<ActiveUnitRecord | null>('tutorial-active-unit', null, activeRecordStorage, {
    getOnInit: true,
});

/** The unit the user is in the middle of (#1854) — all that survives a
 *  reload. Set when a run starts, cleared when it ends. An interrupted run
 *  restarts at the start of this unit: in the tutorial project silently, from
 *  any other Editor through the offer card's resume form.
 *
 *  A unit that ends — or gives way to another — puts back the prefs it wrote
 *  that the user hasn't changed since (the pref ledger, #1858). The same unit
 *  started again keeps its ledger: the first `before` is still the user's. */
export const tutorialActiveUnitAtom = atom(
    get => get(activeRecordAtom)?.unit ?? null,
    (get, set, unit: TutorialUnit | null) => {
        const active = get(activeRecordAtom);
        if (active !== null && active.unit !== unit) {
            for (const { key, value } of prefsToRestore(active.ledger, key => get(prefs[key].atom))) set(prefs[key].atom, value);
        }
        set(activeRecordAtom, unit === null ? null : { unit, ledger: active?.unit === unit ? active.ledger : [] });
    },
);

/** A pref write made by the tutorial, recorded in the active unit's ledger so
 *  the unit's end can undo it. Outside a unit it is not the tutorial's to make. */
export const tutorialPrefWriteAtom = atom(null, (get, set, { key, value }: { key: BooleanPrefKey; value: boolean }) => {
    const active = get(activeRecordAtom);
    if (active === null) return;
    set(activeRecordAtom, { ...active, ledger: recordPrefWrite(active.ledger, key, get(prefs[key].atom), value) });
    set(prefs[key].atom, value);
});

/** Ends the active unit as finished: puts back what the tutorial wrote that
 *  the user hasn't changed since, like any end, and returns the keys in
 *  `asked` (those a step had the user set) left different from before —
 *  the question the end card asks. */
export const tutorialFinishUnitAtom = atom(null, (get, set, asked: ReadonlySet<BooleanPrefKey>): PrefToKeep[] => {
    const active = get(activeRecordAtom);
    if (active === null) return [];
    const { restore, ask } = endOfFinishedUnit(active.ledger, key => get(prefs[key].atom), asked);
    for (const { key, value } of restore) set(prefs[key].atom, value);
    set(activeRecordAtom, null);
    return ask;
});

/** «Вернуть как было»: the asked prefs back to their value before the unit. */
export const tutorialRevertPrefsAtom = atom(null, (_get, set, revert: readonly PrefToKeep[]) => {
    for (const { key, before } of revert) set(prefs[key].atom, before);
});
